import React, { CSSProperties } from "react";
import { FiUser, FiLogOut, FiSun, FiMoon } from "react-icons/fi";
import { FaExclamation } from "react-icons/fa";
import { getThemeColors, getComponentColors, Theme } from "../../theme/colors";
import { useNavigate } from "react-router-dom";
import { CgToolbox } from "react-icons/cg";

interface SidebarFooterProps {
  isCollapsed: boolean;
  colorMode: string;
  username: string;
  devMode: boolean;
  toggleColorMode: () => void;
  handleLogout: () => void;
  setShowDevMenuPopup: (show: boolean) => void;
}

export const SidebarFooter: React.FC<SidebarFooterProps> = ({
  isCollapsed,
  colorMode,
  username,
  devMode,
  toggleColorMode,
  handleLogout,
  setShowDevMenuPopup,
}) => {
  const currentColors = getThemeColors(username, colorMode as Theme);
  const sidebarColors = getComponentColors("sidebar", colorMode as Theme);
  const navigate = useNavigate();
  const buttonStyle: CSSProperties = {
    borderWidth: "1px",
    borderColor: currentColors.border,
    borderRadius: "4px",
    padding: "0.5rem",
    justifyContent: "flex-start",
    transition: "all ease 0.5s",
    backgroundColor: "transparent",
    cursor: "pointer",
    color: currentColors.text,
    overflow: "hidden",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const buttonHoverStyle: CSSProperties = {
    backgroundColor: sidebarColors.buttonHover || currentColors.buttonHover,
  };

  const additionalOptionsStyle: CSSProperties = {
    marginTop: "auto",
    paddingBottom: "1rem",
  };

  const navigateToReadonlyRdm = () => {
    navigate(`/current_rdm`);
  };

  return (
    <div style={additionalOptionsStyle}>
      {!devMode && (
        <div
          style={buttonStyle}
          onClick={() => navigateToReadonlyRdm()}
          title="Readonly RDM"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor =
              buttonHoverStyle.backgroundColor || "";
          }}
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        >
          {!isCollapsed && (
            <>
              <CgToolbox /> <span>RDM</span>
            </>
          )}
        </div>
      )}
      <div
        style={buttonStyle}
        onClick={toggleColorMode}
        title={
          isCollapsed
            ? colorMode === "dark"
              ? "Light mode"
              : "Dark mode"
            : undefined
        }
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor =
            buttonHoverStyle.backgroundColor || "";
        }}
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "transparent")
        }
      >
        {colorMode === "dark" ? <FiSun /> : <FiMoon />}
        {!isCollapsed && (
          <span>{colorMode === "dark" ? "Light mode" : "Dark mode"}</span>
        )}
      </div>

      <div
        style={buttonStyle}
        title={isCollapsed ? username : undefined}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor =
            buttonHoverStyle.backgroundColor || "";
        }}
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "transparent")
        }
      >
        <FiUser />
        {!isCollapsed && <span style={{ flexGrow: 1 }}>{username}</span>}
      </div>

      <div
        style={buttonStyle}
        onClick={handleLogout}
        title={isCollapsed ? "Log Out" : undefined}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor =
            buttonHoverStyle.backgroundColor || "";
        }}
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "transparent")
        }
      >
        <FiLogOut />
        {!isCollapsed && <span>Log Out</span>}
      </div>
      {devMode && (
        <div
          style={buttonStyle}
          onClick={() => setShowDevMenuPopup(true)}
          title={isCollapsed ? "Dev Menu" : undefined}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor =
              buttonHoverStyle.backgroundColor || "";
          }}
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        >
          <FaExclamation />
          {!isCollapsed && <span>Dev Menu</span>}
        </div>
      )}
    </div>
  );
};
